import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { bookingsService } from '../services/api';
import { BarChart3 as BarChart3Icon } from 'lucide-react';

// Excel-like color palette
const Colors = {
  white: '#FFFFFF',
  light: '#F5F3EB',
  border: '#E3DFD3',
  dark: '#24332A',
  primary: '#163626',
  success: '#122E20',
  danger: '#8E3628',
  warning: '#C9903C',
  info: '#5F8A6E',
  secondary: '#5F6B61',
  // Excel-like header colors
  headerBg: '#122E20',
  headerText: '#FFFFFF',
  altRow: '#EEEBE1',
  hoverRow: '#E3DFD3'
};

const Icons = {
  loading: '',
  refresh: '',
  download: '',
  filter: '',
  expand: '▼',
  collapse: '▶'
};

function Reports() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    paymentStatus: '',
    dateFrom: '',
    dateTo: ''
  });

  // Check admin access - Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // TODO: Re-enable auth check after fixing role values in backend
  // const hasAdminAccess = ['PROPERTYADMIN', 'SUPERADMIN', 'PROPERTY_ADMIN', 'SUPER_ADMIN'].includes(user?.role);
  const hasAdminAccess = true; // TEMP: Disabled for dev mode

  useEffect(() => {
    if (hasAdminAccess) {
      fetchBookings();
    }
  }, [hasAdminAccess]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      // Step 1: Fetch all bookings with pagination
      let allBookingsData = [];
      let page = 1;
      let hasMoreData = true;
      const maxPages = 50; // Safety limit
      
      console.log('Step 1: Fetching booking list...');
      
      while (hasMoreData && page <= maxPages) {
        const queryParams = {
          limit: 100,
          page: page,
        };

        try {
          const response = await bookingsService.getAll(queryParams);
          console.log(`Fetched page ${page}:`, response);
          
          // Handle different response formats
          const bookingsData = response.data?.bookings || response.bookings || response.data || response || [];
          
          if (Array.isArray(bookingsData) && bookingsData.length > 0) {
            allBookingsData = [...allBookingsData, ...bookingsData];
            console.log(`Page ${page}: Got ${bookingsData.length} bookings. Total: ${allBookingsData.length}`);
            
            // If we got less than the limit, we've reached the end
            if (bookingsData.length < 100) {
              hasMoreData = false;
            } else {
              page++;
            }
          } else {
            hasMoreData = false;
          }
        } catch (pageError) {
          console.error(`Error loading page ${page}:`, pageError);
          hasMoreData = false;
        }
      }
      
      console.log(`Total bookings fetched: ${allBookingsData.length}`);
      
      if (allBookingsData.length === 0) {
        setBookings([]);
        toast.info('No bookings found');
        setLoading(false);
        return;
      }
      
      // Step 2: Fetch detailed information for each booking
      console.log(`Step 2: Fetching detailed data for ${allBookingsData.length} bookings...`);
      
      const detailedBookings = [];
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < allBookingsData.length; i++) {
        const booking = allBookingsData[i];
        try {
          console.log(`Fetching details for booking ${i + 1}/${allBookingsData.length}: ${booking.bookingNumber || booking.id}`);
          
          const detailsResponse = await bookingsService.getAdminBookingDetails(booking.id);
          console.log(`Got details for ${booking.bookingNumber}:`, detailsResponse);
          
          // Handle different response formats
          const detailedData = detailsResponse.data || detailsResponse;
          
          detailedBookings.push(detailedData);
          successCount++;
          
          // Update progress toast every 10 bookings
          if ((i + 1) % 10 === 0 || i === allBookingsData.length - 1) {
            toast.info(`Loading details: ${i + 1}/${allBookingsData.length}`, {
              position: "top-right",
              autoClose: 1000,
              hideProgressBar: true,
            });
          }
        } catch (detailError) {
          console.error(`Error fetching details for booking ${booking.bookingNumber || booking.id}:`, detailError);
          errorCount++;
          
          // Still add the basic booking data if detailed fetch fails
          detailedBookings.push({
            booking: booking,
            guest: booking.guest || {},
            pricing: booking.pricing || {},
            summary: booking.summary || {},
            bookedRooms: [],
            bookedBeds: [],
            bookedCourses: [],
            addOns: [],
            payments: [],
            refunds: []
          });
        }
      }
      
      console.log(`Successfully fetched details: ${successCount}/${allBookingsData.length}`);
      if (errorCount > 0) {
        console.warn(`Failed to fetch details for ${errorCount} bookings`);
      }
      
      setBookings(detailedBookings);
      
      toast.success(`Loaded ${detailedBookings.length} bookings with full details`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings data');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (bookingId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedRows(newExpanded);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch {
      return '-';
    }
  };

  const getFilteredBookings = () => {
    return bookings.filter(booking => {
      const matchesSearch = !filters.search || 
        booking.booking?.bookingNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
        booking.guest?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        booking.guest?.email?.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesStatus = !filters.status || booking.booking?.status === filters.status;
      const matchesPaymentStatus = !filters.paymentStatus || booking.booking?.paymentStatus === filters.paymentStatus;
      
      return matchesSearch && matchesStatus && matchesPaymentStatus;
    });
  };

  const exportToCSV = () => {
    const filteredBookings = getFilteredBookings();
    if (filteredBookings.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = [
      'Booking Number', 'Status', 'Payment Status', 'Guest Name', 'Guest Email', 'Guest Phone',
      'Check In', 'Check Out', 'Nights', 'Guest Count',
      'Rooms', 'Room Numbers', 'Room Types', 'Rooms Total',
      'Beds', 'Bed Numbers', 'Room Numbers (Beds)', 'Beds Total',
      'Courses', 'Course Names', 'Course Quantity', 'Courses Total',
      'Add-ons', 'Add-on Names', 'Add-on Quantities', 'Add-ons Total',
      'Total Price', 'Final Amount', 'Accommodation Total', 'Grand Total',
      'Payments', 'Payment Numbers', 'Payment Amounts', 'Payment Methods', 'Total Paid',
      'Refunds', 'Refund Numbers', 'Refund Amounts', 'Refund Methods', 'Total Refunded',
      'Net Received', 'Remaining Balance',
      'Created At'
    ];

    const rows = filteredBookings.map(booking => {
      const b = booking.booking || {};
      const g = booking.guest || {};
      const p = booking.pricing || {};
      const s = booking.summary || {};
      
      const rooms = booking.bookedRooms || [];
      const beds = booking.bookedBeds || [];
      const courses = booking.bookedCourses || [];
      const addOns = booking.addOns || [];
      const payments = booking.payments || [];
      const refunds = booking.refunds || [];

      return [
        b.bookingNumber || '-',
        b.status || '-',
        b.paymentStatus || '-',
        g.name || '-',
        g.email || '-',
        g.phone || '-',
        formatDate(b.checkIn),
        formatDate(b.checkOut),
        b.nights || 0,
        b.guestCount || 0,
        s.totalRooms || rooms.length,
        rooms.map(r => r.roomNumber).join(', ') || '-',
        rooms.map(r => r.roomTypeName).join(', ') || '-',
        p.roomsTotal || 0,
        s.totalBeds || beds.length,
        beds.map(bed => bed.bedNumber).join(', ') || '-',
        beds.map(bed => bed.roomNumber).join(', ') || '-',
        p.bedsTotal || 0,
        s.totalCourses || courses.length,
        courses.map(c => c.courseName).join(', ') || '-',
        courses.reduce((sum, c) => sum + (c.quantity || 0), 0),
        p.coursesTotal || 0,
        s.totalAddOns || addOns.length,
        addOns.map(a => a.service?.name || a.item?.name || '-').join(', ') || '-',
        addOns.map(a => a.quantity).join(', ') || '-',
        p.addOnsTotal || 0,
        p.totalPrice || 0,
        p.finalAmount || 0,
        p.accommodationTotal || 0,
        p.grandTotal || 0,
        s.totalPayments || payments.length,
        payments.map(pay => pay.paymentNumber).join(', ') || '-',
        payments.map(pay => pay.amount).join(', ') || '-',
        payments.map(pay => pay.method).join(', ') || '-',
        s.totalPaid || 0,
        s.totalRefunds || refunds.length,
        refunds.map(r => r.refundNumber).join(', ') || '-',
        refunds.map(r => r.amount).join(', ') || '-',
        refunds.map(r => r.method).join(', ') || '-',
        s.totalRefunded || 0,
        s.netReceived || 0,
        s.remainingBalance || 0,
        formatDate(b.createdAt)
      ];
    });

    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    toast.success(`Exported ${filteredBookings.length} bookings to CSV`);
  };

  // TEMP: Access check disabled for dev mode
  // if (!hasAdminAccess) {
  //   return (
  //     <div style={{ padding: '24px', textAlign: 'center' }}>
  //       <h2 style={{ color: Colors.danger }}>Access Denied</h2>
  //       <p>You need Property Admin or Super Admin access to view reports.</p>
  //     </div>
  //   );
  // }

  const filteredBookings = getFilteredBookings();

  // TEMP: Commented out for dev mode
  // if (!hasAdminAccess) {
  //   return (
  //     <div style={{ padding: '24px', textAlign: 'center' }}>
  //       <h2 style={{ color: Colors.danger }}>Access Denied</h2>
  //       <p>You need Property Admin or Super Admin access to view reports.</p>
  //     </div>
  //   );
  // }

  return (
    <div style={{ padding: '16px', backgroundColor: Colors.light, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        backgroundColor: Colors.white,
        padding: '16px 20px',
        marginBottom: '16px',
        borderRadius: '4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: Colors.dark }}>
            Booking Reports
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: Colors.secondary }}>
            Excel-like view • {filteredBookings.length} of {bookings.length} bookings
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={fetchBookings}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: Colors.primary,
              color: Colors.white,
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? Icons.loading : Icons.refresh} Refresh
          </button>
          <button
            onClick={exportToCSV}
            disabled={filteredBookings.length === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: Colors.success,
              color: Colors.white,
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: filteredBookings.length === 0 ? 'not-allowed' : 'pointer',
              opacity: filteredBookings.length === 0 ? 0.6 : 1
            }}
          >
            {Icons.download} Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: Colors.white,
        padding: '16px 20px',
        marginBottom: '16px',
        borderRadius: '4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: Colors.dark }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Booking number, guest name, email..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: Colors.dark }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="CHECKEDIN">Checked In</option>
              <option value="CHECKEDOUT">Checked Out</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: Colors.dark }}>
              Payment Status
            </label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">All Payment Status</option>
              <option value="PENDING">Pending</option>
              <option value="PARTIAL">Partial</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setFilters({ search: '', status: '', paymentStatus: '', dateFrom: '', dateTo: '' })}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: Colors.secondary,
                color: Colors.white,
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          backgroundColor: Colors.white,
          padding: '40px',
          borderRadius: '4px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>{Icons.loading}</div>
          <p style={{ fontSize: '16px', color: Colors.secondary }}>Loading bookings data...</p>
        </div>
      )}

      {/* Excel-like Table */}
      {!loading && filteredBookings.length > 0 && (
        <div style={{
          backgroundColor: Colors.white,
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={headerStyle}>Actions</th>
                  <th style={headerStyle}>Booking #</th>
                  <th style={headerStyle}>Status</th>
                  <th style={headerStyle}>Payment</th>
                  <th style={headerStyle}>Guest Name</th>
                  <th style={headerStyle}>Email</th>
                  <th style={headerStyle}>Phone</th>
                  <th style={headerStyle}>Check In</th>
                  <th style={headerStyle}>Check Out</th>
                  <th style={headerStyle}>Nights</th>
                  <th style={headerStyle}>Guests</th>
                  <th style={headerStyle}>Rooms</th>
                  <th style={headerStyle}>Beds</th>
                  <th style={headerStyle}>Courses</th>
                  <th style={headerStyle}>Add-ons</th>
                  <th style={headerStyle}>Total Price</th>
                  <th style={headerStyle}>Accommodation</th>
                  <th style={headerStyle}>Grand Total</th>
                  <th style={headerStyle}>Paid</th>
                  <th style={headerStyle}>Refunded</th>
                  <th style={headerStyle}>Net Received</th>
                  <th style={headerStyle}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking, index) => {
                  const b = booking.booking || {};
                  const g = booking.guest || {};
                  const p = booking.pricing || {};
                  const s = booking.summary || {};
                  const isExpanded = expandedRows.has(b.id);

                  return (
                    <React.Fragment key={b.id || index}>
                      {/* Main Row */}
                      <tr style={{
                        backgroundColor: index % 2 === 0 ? Colors.white : Colors.altRow,
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Colors.hoverRow}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? Colors.white : Colors.altRow}
                      >
                        <td style={cellStyle} onClick={() => toggleRowExpansion(b.id)}>
                          <span style={{ fontSize: '12px' }}>
                            {isExpanded ? Icons.expand : Icons.collapse}
                          </span>
                        </td>
                        <td style={{...cellStyle, fontWeight: '600', color: Colors.primary}}>{b.bookingNumber || '-'}</td>
                        <td style={cellStyle}>
                          <StatusBadge status={b.status} />
                        </td>
                        <td style={cellStyle}>
                          <StatusBadge status={b.paymentStatus} type="payment" />
                        </td>
                        <td style={cellStyle}>{g.name || '-'}</td>
                        <td style={cellStyle}>{g.email || '-'}</td>
                        <td style={cellStyle}>{g.phone || '-'}</td>
                        <td style={cellStyle}>{formatDate(b.checkIn)}</td>
                        <td style={cellStyle}>{formatDate(b.checkOut)}</td>
                        <td style={{...cellStyle, textAlign: 'center'}}>{b.nights || 0}</td>
                        <td style={{...cellStyle, textAlign: 'center'}}>{b.guestCount || 0}</td>
                        <td style={{...cellStyle, textAlign: 'center'}}>{s.totalRooms || 0}</td>
                        <td style={{...cellStyle, textAlign: 'center'}}>{s.totalBeds || 0}</td>
                        <td style={{...cellStyle, textAlign: 'center'}}>{s.totalCourses || 0}</td>
                        <td style={{...cellStyle, textAlign: 'center'}}>{s.totalAddOns || 0}</td>
                        <td style={{...cellStyle, fontWeight: '600', textAlign: 'right'}}>{formatCurrency(p.totalPrice)}</td>
                        <td style={{...cellStyle, textAlign: 'right'}}>{formatCurrency(p.accommodationTotal)}</td>
                        <td style={{...cellStyle, fontWeight: '700', color: Colors.success, textAlign: 'right'}}>{formatCurrency(p.grandTotal)}</td>
                        <td style={{...cellStyle, color: Colors.success, textAlign: 'right'}}>{formatCurrency(s.totalPaid)}</td>
                        <td style={{...cellStyle, color: Colors.danger, textAlign: 'right'}}>{formatCurrency(s.totalRefunded)}</td>
                        <td style={{...cellStyle, fontWeight: '600', textAlign: 'right'}}>{formatCurrency(s.netReceived)}</td>
                        <td style={{...cellStyle, fontWeight: '600', color: s.remainingBalance > 0 ? Colors.danger : Colors.success, textAlign: 'right'}}>
                          {formatCurrency(s.remainingBalance)}
                        </td>
                      </tr>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="22" style={{ padding: '16px', backgroundColor: Colors.light, borderBottom: `2px solid ${Colors.border}` }}>
                            <ExpandedDetails booking={booking} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!loading && filteredBookings.length === 0 && (
        <div style={{
          backgroundColor: Colors.white,
          padding: '40px',
          borderRadius: '4px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}><BarChart3Icon size="1em" aria-hidden="true" /></div>
          <h3 style={{ margin: '0 0 8px 0', color: Colors.dark }}>No Bookings Found</h3>
          <p style={{ margin: 0, color: Colors.secondary }}>
            {bookings.length === 0 
              ? 'No bookings available. Start by creating your first booking.'
              : 'No bookings match your current filters. Try adjusting your search criteria.'}
          </p>
        </div>
      )}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status, type = 'booking' }) {
  if (!status) return <span>-</span>;

  let color = Colors.secondary;
  if (type === 'booking') {
    if (status === 'CONFIRMED' || status === 'COMPLETED') color = Colors.success;
    else if (status === 'PENDING') color = Colors.warning;
    else if (status === 'CANCELLED') color = Colors.danger;
    else if (status === 'CHECKEDIN') color = Colors.info;
  } else if (type === 'payment') {
    if (status === 'SUCCESS') color = Colors.success;
    else if (status === 'PARTIAL' || status === 'PENDING') color = Colors.warning;
    else if (status === 'FAILED') color = Colors.danger;
  }

  return (
    <span style={{
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: color,
      color: Colors.white,
      textTransform: 'uppercase',
      display: 'inline-block'
    }}>
      {status}
    </span>
  );
}

// Expanded Details Component
function ExpandedDetails({ booking }) {
  const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;
  
  const rooms = booking.bookedRooms || [];
  const beds = booking.bookedBeds || [];
  const courses = booking.bookedCourses || [];
  const addOns = booking.addOns || [];
  const payments = booking.payments || [];
  const refunds = booking.refunds || [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
      {/* Rooms Section */}
      {rooms.length > 0 && (
        <DetailSection title="Booked Rooms" color="#E3DFD3">
          {rooms.map((room, idx) => (
            <div key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: Colors.white, borderRadius: '4px' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>{room.roomNumber} - {room.roomTypeName}</div>
              <div style={{ fontSize: '12px', color: Colors.secondary }}>
                <div>Nights: {room.nights} | Base: {formatCurrency(room.basePrice)}</div>
                <div>Final Amount: <span style={{ fontWeight: '600', color: Colors.success }}>{formatCurrency(room.finalAmount)}</span></div>
                {room.priceDetails && (
                  <div style={{ marginTop: '6px' }}>
                    <div>Subtotal: {formatCurrency(room.priceDetails.subtotal)}</div>
                    <div>Taxes: {formatCurrency(room.priceDetails.totalTaxes)}</div>
                    <div>Fees: {formatCurrency(room.priceDetails.totalFees)}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Beds Section */}
      {beds.length > 0 && (
        <DetailSection title="Booked Beds" color="#E1E9E2">
          {beds.map((bed, idx) => (
            <div key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: Colors.white, borderRadius: '4px' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>Bed {bed.bedNumber} - {bed.roomNumber}</div>
              <div style={{ fontSize: '12px', color: Colors.secondary }}>
                <div>{bed.roomTypeName}</div>
                <div>Nights: {bed.nights} | Base: {formatCurrency(bed.basePrice)}</div>
                <div>Final Amount: <span style={{ fontWeight: '600', color: Colors.success }}>{formatCurrency(bed.finalAmount)}</span></div>
              </div>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Courses Section */}
      {courses.length > 0 && (
        <DetailSection title="Booked Courses" color="#E5B3A9">
          {courses.map((course, idx) => (
            <div key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: Colors.white, borderRadius: '4px' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>{course.courseName}</div>
              <div style={{ fontSize: '12px', color: Colors.secondary }}>
                <div>Quantity: {course.quantity} | Fee: {formatCurrency(course.fee)}</div>
                <div>Start: {format(new Date(course.startDate), 'dd-MMM-yyyy')}</div>
                <div>End: {format(new Date(course.endDate), 'dd-MMM-yyyy')}</div>
                <div>Final Amount: <span style={{ fontWeight: '600', color: Colors.success }}>{formatCurrency(course.finalAmount)}</span></div>
              </div>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Add-ons Section */}
      {addOns.length > 0 && (
        <DetailSection title="Add-on Services" color="#F3E6CB">
          {addOns.map((addon, idx) => (
            <div key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: Colors.white, borderRadius: '4px' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>
                {addon.service?.name || addon.item?.name || 'Unknown Service'}
              </div>
              <div style={{ fontSize: '12px', color: Colors.secondary }}>
                <div>Quantity: {addon.quantity} | Price: {formatCurrency(addon.price)}</div>
                <div>Final Amount: <span style={{ fontWeight: '600', color: Colors.success }}>{formatCurrency(addon.finalAmount)}</span></div>
              </div>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Payments Section */}
      {payments.length > 0 && (
        <DetailSection title="Payments" color="#E3DFD3">
          {payments.map((payment, idx) => (
            <div key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: Colors.white, borderRadius: '4px' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>{payment.paymentNumber}</div>
              <div style={{ fontSize: '12px', color: Colors.secondary }}>
                <div>Amount: <span style={{ fontWeight: '600', color: Colors.success }}>{formatCurrency(payment.amount)}</span></div>
                <div>Method: {payment.method?.toUpperCase()} | Status: {payment.status}</div>
                <div>Date: {format(new Date(payment.createdAt), 'dd-MMM-yyyy HH:mm')}</div>
                {payment.transactionId && <div>Transaction: {payment.transactionId}</div>}
              </div>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Refunds Section */}
      {refunds.length > 0 && (
        <DetailSection title="Refunds" color="#E5B3A9">
          {refunds.map((refund, idx) => (
            <div key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: Colors.white, borderRadius: '4px' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>{refund.refundNumber}</div>
              <div style={{ fontSize: '12px', color: Colors.secondary }}>
                <div>Amount: <span style={{ fontWeight: '600', color: Colors.danger }}>{formatCurrency(refund.amount)}</span></div>
                <div>Method: {refund.method?.toUpperCase()} | Status: {refund.status}</div>
                <div>Reason: {refund.reason}</div>
                <div>Date: {format(new Date(refund.createdAt), 'dd-MMM-yyyy HH:mm')}</div>
              </div>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Price Breakdown */}
      {booking.pricing?.breakdown && (
        <DetailSection title="Price Breakdown" color="#E3DFD3">
          <div style={{ padding: '10px', backgroundColor: Colors.white, borderRadius: '4px', fontSize: '13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Rooms Total:</span>
              <span style={{ fontWeight: '600' }}>{formatCurrency(booking.pricing.roomsTotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Beds Total:</span>
              <span style={{ fontWeight: '600' }}>{formatCurrency(booking.pricing.bedsTotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Courses Total:</span>
              <span style={{ fontWeight: '600' }}>{formatCurrency(booking.pricing.coursesTotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Add-ons Total:</span>
              <span style={{ fontWeight: '600' }}>{formatCurrency(booking.pricing.addOnsTotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: `2px solid ${Colors.border}` }}>
              <span style={{ fontWeight: '700' }}>Grand Total:</span>
              <span style={{ fontWeight: '700', color: Colors.success }}>{formatCurrency(booking.pricing.grandTotal)}</span>
            </div>
          </div>
        </DetailSection>
      )}
    </div>
  );
}

// Detail Section Component
function DetailSection({ title, color, children }) {
  return (
    <div style={{
      border: `1px solid ${Colors.border}`,
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: color,
        padding: '10px 12px',
        fontWeight: '600',
        fontSize: '14px',
        borderBottom: `1px solid ${Colors.border}`
      }}>
        {title}
      </div>
      <div style={{ padding: '12px' }}>
        {children}
      </div>
    </div>
  );
}

// Shared styles
const headerStyle = {
  backgroundColor: Colors.headerBg,
  color: Colors.headerText,
  padding: '12px 8px',
  fontSize: '12px',
  fontWeight: '600',
  textAlign: 'left',
  borderBottom: `2px solid ${Colors.border}`,
  whiteSpace: 'nowrap'
};

const cellStyle = {
  padding: '10px 8px',
  borderBottom: `1px solid ${Colors.border}`,
  fontSize: '13px',
  whiteSpace: 'nowrap'
};

export default Reports;
